<div class="product-detail-page" *ngIf="!isLoading && product; else loadOrError">
  <div class="container">
    <nav class="breadcrumb">
      <a routerLink="/products">Products</a>
      <span class="sep">/</span>
      <span>{{ product.category }}</span>
      <span class="sep">/</span>
      <span>{{ product.name }}</span>
    </nav>

    <!-- Top: Image slider left, product info right -->
    <div class="detail-hero">
      <div class="image-slider">
        <div class="slider-main-wrap">
          <button type="button" class="slider-arrow slider-arrow-prev" (click)="prevImage()" aria-label="Previous image">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div class="slider-main">
            <img [src]="getImgSrc(productImages[currentImageIndex()])" [alt]="product.name" class="slider-main-img">
            <div *ngIf="product.isPopular" class="popular-badge">Featured</div>
          </div>
          <button type="button" class="slider-arrow slider-arrow-next" (click)="nextImage()" aria-label="Next image">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
        <div class="slider-thumbs" *ngIf="productImages.length > 1">
          @for (img of productImages; track $index) {
            <button type="button" class="thumb-btn" [class.active]="currentImageIndex() === $index" (click)="setImage($index)">
              <img [src]="getImgSrc(img)" [alt]="product.name + ' thumbnail ' + $index">
            </button>
          }
        </div>
      </div>

      <div class="detail-content">
        <span class="category-tag">{{ product.category }}</span>
        <h1 class="product-title">{{ product.name }}</h1>
        <p class="product-description">{{ product.description }}</p>

        <!-- Review stars -->
        @if (product.rating != null) {
          <div class="review-stars">
            @for (star of ratingStars; track $index) {
              @switch (star) {
                @case ('full') { <span class="star star-full">★</span> }
                @case ('half') { <span class="star star-half">★</span> }
                @case ('empty') { <span class="star star-empty">★</span> }
              }
            }
            <span class="review-count">({{ product.reviewCount ?? 0 }} reviews)</span>
          </div>
        }

        <p class="product-price">{{ product.price | currency }}</p>

        <!-- Enquiry section -->
        <div class="enquiry-section">
          <p class="enquiry-text">Purchase in bakery or get it delivered</p>
          <a href="#order" class="btn btn-primary">Find your local bakery</a>
          <p class="enquiry-note">Contact your bakery for product availability and local prices.</p>
        </div>

        <a routerLink="/products" class="btn btn-outline">← Back to Products</a>
      </div>
    </div>

    <!-- Bottom: Left-right layout - tabs left, content right -->
    <div class="detail-info-section">
      <nav class="info-tabs">
        <button type="button" class="info-tab" [class.active]="isSelected('description')" (click)="selectSection('description')">
          <span class="tab-icon tab-icon-bread" aria-hidden="true"></span>
          <span>Description</span>
        </button>
        <button type="button" class="info-tab" [class.active]="isSelected('ingredients')" (click)="selectSection('ingredients')">
          <span class="tab-icon tab-icon-leaf" aria-hidden="true"></span>
          <span>Ingredients</span>
        </button>
        <button type="button" class="info-tab" [class.active]="isSelected('nutrition')" (click)="selectSection('nutrition')">
          <span class="tab-icon tab-icon-bowl" aria-hidden="true"></span>
          <span>Nutritional Info</span>
        </button>
      </nav>
      <div class="info-content">
        @switch (expandedSection()) {
          @case ('description') {
            <p class="info-text">{{ product.fullDescription || product.description }}</p>
          }
          @case ('ingredients') {
            @if (product.ingredients?.length) {
              <ul class="ingredients-list">
                @for (ing of product.ingredients; track ing) {
                  <li>{{ ing }}</li>
                }
              </ul>
            } @else {
              <p class="info-text">Ingredients not specified.</p>
            }
          }
          @case ('nutrition') {
            @if (product.nutritionalInfo && product.nutritionalInfo.length > 0) {
              <div class="nutrition-table">
                <div class="nutrition-grid">
                  @for (item of product.nutritionalInfo; track item.label) {
                    <div class="nutrition-row">
                      <span>{{ item.label }}</span>
                      <span>{{ item.value }}</span>
                    </div>
                  }
                </div>
              </div>
            } @else {
              <p class="info-text">Nutritional information not available.</p>
            }
          }
        }
      </div>
    </div>

    <!-- Similar Products Slider -->
    @if (similarProducts.length > 0) {
      <section class="similar-products">
        <h2 class="similar-title">You might also like</h2>
        <div class="similar-slider-wrap">
          <button type="button" class="slider-arrow" (click)="similarPrev()" aria-label="Previous products">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div class="similar-track" #similarTrack>
            @for (p of similarProducts; track p.id) {
              <a [routerLink]="['/product', p.id]" class="similar-card">
                <div class="similar-card__img">
                  <img [src]="getImgSrc(p.imageUrl)" [alt]="p.name">
                  @if (p.isPopular) { <span class="similar-badge">Popular</span> }
                </div>
                <div class="similar-card__info">
                  <span class="similar-card__cat">{{ p.category }}</span>
                  <h3 class="similar-card__name">{{ p.name }}</h3>
                </div>
              </a>
            }
          </div>
          <button type="button" class="slider-arrow" (click)="similarNext()" aria-label="Next products">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
        <a routerLink="/products" class="similar-link">View all products →</a>
      </section>
    }
  </div>
</div>

<ng-template #loadOrError>
  <div class="load-error section-padding text-center">
    <div *ngIf="isLoading" class="spinner"></div>
    <div *ngIf="error">
      <h2>Product not found</h2>
      <a routerLink="/products" class="btn btn-primary">Back to Products</a>
    </div>
  </div>
</ng-template>
